import Modal from 'components/Modal';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Logo from '@alltrails/shared/denali/components/Logo';
import Typography from 'components/Typography';
import badgeCheckSrc from 'components/icons/badge-check.svg';
import { wrapUrlSafe } from 'utils/language_support_util';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { Fragment, Key, useEffect } from 'react';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import PurchaseScreenTypePlan from '@alltrails/analytics/enums/PurchaseScreenTypePlan';
import logWebProModalPurchaseButtonTapped from '@alltrails/analytics/events/logWebProModalPurchaseButtonTapped';
import logWebProModalShown from '@alltrails/analytics/events/logWebProModalShown';
import WebProModalPurchaseButtonCopyText from '@alltrails/analytics/enums/WebProModalPurchaseButtonCopyText';
import useUser from 'hooks/useUser';
import * as styles from './FlyoverUpsell.module.scss';

const features = [
  <FormattedMessage defaultMessage="Download offline maps" />,
  <FormattedMessage defaultMessage="Get alerts for wrong turns" />,
  <FormattedMessage defaultMessage="Find trails by distance from you" />
];

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  flyoverHref: string;
};

// Desktop trial: https://www.figma.com/file/oniF4lWHR9LKNsYuFSlI1s/Flyovers?node-id=2804%3A199213&t=VbwNZSrqsexKiBYN-4
// Mobile trial: https://www.figma.com/file/oniF4lWHR9LKNsYuFSlI1s/Flyovers?node-id=2804%3A200250&t=VbwNZSrqsexKiBYN-4
// Desktop non-trial: https://www.figma.com/file/oniF4lWHR9LKNsYuFSlI1s/Flyovers?node-id=2772%3A200613&t=VbwNZSrqsexKiBYN-4
// Mobile non-trial: https://www.figma.com/file/oniF4lWHR9LKNsYuFSlI1s/Flyovers?node-id=1634%3A200518&t=VbwNZSrqsexKiBYN-4
export default function FlyoverUpsell({ isOpen, onRequestClose, flyoverHref }: Props) {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();

  const isPromoEligible = !user || user.isPromoEligible;

  useEffect(() => {
    logWebProModalShown({
      purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
      trigger: CarouselDisplayTrigger.Flyover
    });
  }, [isPromoEligible]);

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: __AT_DATA__.currencyCode
  });

  const { plan } = __AT_DATA__;

  const params = new URLSearchParams();
  params.set('returnTo', flyoverHref);

  const href = wrapUrlSafe(`/plus?${params.toString()}`, languageRegionCode);

  return (
    <Modal
      background="cream"
      baseCtas={{
        finePrint: isPromoEligible ? (
          <FormattedMessage
            defaultMessage="Get 7 days free, then only {annualCost}/year (that’s {monthlyCost}/month). Cancel anytime."
            values={{
              annualCost: formatter.format(Number(plan.priceTotal)),
              monthlyCost: formatter.format(Number(plan.priceMonth))
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Only {annualCost}/year (that’s {monthlyCost}/month). Cancel anytime."
            values={{
              annualCost: formatter.format(Number(plan.priceTotal)),
              monthlyCost: formatter.format(Number(plan.priceMonth))
            }}
          />
        ),
        orientation: 'vertical',
        primaryCta: {
          linkInfo: { href },
          onClick: () => {
            logWebProModalPurchaseButtonTapped({
              copy_text: isPromoEligible
                ? WebProModalPurchaseButtonCopyText.StartYourFreeTrial
                : WebProModalPurchaseButtonCopyText.UpgradeToAllTrailsPlus,
              purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
              trigger: CarouselDisplayTrigger.Flyover
            });
          },
          text: isPromoEligible ? (
            <FormattedMessage defaultMessage="Start your free trial" />
          ) : (
            <FormattedMessage defaultMessage="Upgrade to AllTrails+" />
          ),
          testId: 'flyover-upsell-cta'
        }
      }}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size="lg"
      headerClassName={styles.header}
      iconButtonVariant="default"
      closeButtonStyle={styles.close}
    >
      <div className={styles.content}>
        <div className={styles.title}>
          <Logo className={styles.logo} color="dark" size="sm" variant="plus-logomark" />
          <Typography component="div" variant="heading700">
            <FormattedMessage defaultMessage="Preview any trail with a 3D flyover" />
          </Typography>
          <Typography component="div" variant="text300">
            <FormattedMessage defaultMessage="Immerse yourself in a 3D preview to anticipate every twist and turn. Get AllTrails+ to unlock this feature and more:" />
          </Typography>
        </div>
        <div className={styles.features}>
          {features.map((feature, index) => (
            <Fragment key={index as Key}>
              <img alt="" src={badgeCheckSrc} />
              <Typography component="div" variant="text300">
                {feature}
              </Typography>
            </Fragment>
          ))}
        </div>
      </div>
    </Modal>
  );
}
